import { apiRequest } from "./client";

// ---- sharing (owner) ---------------------------------------------------------

export type ShareRole = "viewer" | "editor" | "co_owner";

export interface UserHit {
  id: string;
  name: string;
  email: string;
  can_use_platform: boolean;
}

export interface SharePerson {
  user_id: string;
  name: string;
  email: string;
  role: ShareRole | "owner" | "custom";
  has_publish: boolean;
  is_creator: boolean;
  is_me: boolean;
}

export interface Sharing {
  report_id: string;
  owner_name: string | null;
  my_role: "admin" | "owner" | "co_owner" | "editor" | "viewer" | "custom";
  can_manage: boolean;
  people: SharePerson[];
}

export const ROLE_LABEL: Record<string, string> = {
  owner: "Owner",
  co_owner: "Co-owner",
  editor: "Editor",
  viewer: "Viewer",
  custom: "Custom (set by IT)",
  admin: "Administrator",
};

export const ROLE_HINT: Record<ShareRole, string> = {
  viewer: "Can open and use the report.",
  editor: "Can also change the draft.",
  co_owner: "Can also decide who the report is shared with.",
};

export const sharingApi = {
  searchPeople: (q: string) => apiRequest<UserHit[]>(`/api/v1/owner/people/search?q=${encodeURIComponent(q)}`),
  get: (reportId: string) => apiRequest<Sharing>(`/api/v1/owner/reports/${reportId}/sharing`),
  setPerson: (reportId: string, userId: string, role: ShareRole) =>
    apiRequest<Sharing>(`/api/v1/owner/reports/${reportId}/sharing/people`, { method: "PUT", body: { user_id: userId, role } }),
  removePerson: (reportId: string, userId: string) =>
    apiRequest<Sharing>(`/api/v1/owner/reports/${reportId}/sharing/people/${userId}`, { method: "DELETE" }),
};

// ---- approvals -----------------------------------------------------------------

export type RequestKind = "share_link" | "tv_publish" | "access";
export type RequestState = "pending" | "approved" | "rejected" | "cancelled";

export interface RequestItem {
  id: string;
  kind: RequestKind;
  report_id: string;
  report_name: string | null;
  requested_by: string;
  requested_by_name: string | null;
  status: RequestState;
  created_at: string;
  decided_by_name: string | null;
  decided_at: string | null;
  decision_note: string | null;
  recipients: string | null;
  reason: string | null;
  expires_in_days: number | null;
  link_id: string | null;
  link_ready: boolean;
  note: string | null;
  draft_revision: number | null;
  published_version_number: number | null;
  device_group_ids: string[];
  device_ids: string[];
  target_names: string[];
  stale: boolean;
  message: string | null;
}

export interface LinkDelivery {
  token: string;
  path: string;
  expires_at: string | null;
}

export interface ScreenChoices {
  groups: { id: string; name: string }[];
  devices: { id: string; name: string; group_id: string | null }[];
}

export const ownerApprovalsApi = {
  requestLink: (reportId: string, body: { recipients: string; reason: string; expires_in_days: number | null }) =>
    apiRequest<RequestItem>(`/api/v1/owner/reports/${reportId}/share-link-requests`, { method: "POST", body }),
  listLinkRequests: (reportId: string) => apiRequest<RequestItem[]>(`/api/v1/owner/reports/${reportId}/share-link-requests`),
  cancelLinkRequest: (id: string) => apiRequest<RequestItem>(`/api/v1/owner/share-link-requests/${id}/cancel`, { method: "POST" }),
  collectLink: (id: string) => apiRequest<LinkDelivery>(`/api/v1/owner/share-link-requests/${id}/collect-link`, { method: "POST" }),
  requestTvPublish: (reportId: string, body: { note?: string; device_group_ids: string[]; device_ids: string[] }) =>
    apiRequest<RequestItem>(`/api/v1/owner/reports/${reportId}/tv-publish-requests`, { method: "POST", body }),
  listTvRequests: (reportId: string) => apiRequest<RequestItem[]>(`/api/v1/owner/reports/${reportId}/tv-publish-requests`),
  cancelTvRequest: (id: string) => apiRequest<RequestItem>(`/api/v1/owner/tv-publish-requests/${id}/cancel`, { method: "POST" }),
  myRequests: () => apiRequest<RequestItem[]>("/api/v1/owner/my-requests"),
  screens: () => apiRequest<ScreenChoices>("/api/v1/owner/screens"),
};

const KIND_PATH: Record<RequestKind, string> = { share_link: "share-link", tv_publish: "tv-publish", access: "access" };

export const adminApprovalsApi = {
  list: (state: RequestState | "all" = "pending") => apiRequest<RequestItem[]>(`/api/v1/admin/approvals?state=${state}`),
  count: () => apiRequest<{ share_link: number; tv_publish: number; access: number; total: number }>("/api/v1/admin/approvals/count"),
  approve: (item: Pick<RequestItem, "kind" | "id">, note?: string) =>
    apiRequest<RequestItem>(`/api/v1/admin/approvals/${KIND_PATH[item.kind]}/${item.id}/approve`, { method: "POST", body: { note: note || null } }),
  reject: (item: Pick<RequestItem, "kind" | "id">, note: string) =>
    apiRequest<RequestItem>(`/api/v1/admin/approvals/${KIND_PATH[item.kind]}/${item.id}/reject`, { method: "POST", body: { note } }),
};

// ---- viewer library --------------------------------------------------------------

export interface LibraryItem {
  id: string;
  name: string;
  description: string | null;
  category_name: string | null;
  owner_id: string;
  owner_name: string | null;
  my_role: string;
  owned_by_me: boolean;
  shared_with_me: boolean;
  favourite: boolean;
  last_opened_at: string | null;
  published: boolean;
  version: number | null;
  last_published_at: string | null;
  updated_at: string;
  data_as_of: string | null;
}

export interface DiscoverItem {
  id: string;
  name: string;
  owner_name: string | null;
  category_name: string | null;
  request_pending: boolean;
}

export const libraryApi = {
  list: () => apiRequest<LibraryItem[]>("/api/v1/viewer/library"),
  setFavourite: (id: string, on: boolean) =>
    apiRequest<{ favourite: boolean }>(`/api/v1/viewer/reports/${id}/favourite`, { method: "PUT", body: { on } }),
  discover: (q: string) => apiRequest<DiscoverItem[]>(`/api/v1/viewer/library/discover?q=${encodeURIComponent(q)}`),
  requestAccess: (id: string, message?: string) =>
    apiRequest<RequestItem>(`/api/v1/viewer/reports/${id}/access-requests`, { method: "POST", body: { message: message || null } }),
};

// ---- notifications -----------------------------------------------------------------

export interface NotificationItem {
  id: string;
  kind: string;
  title: string;
  detail: string | null;
  at: string;
  unread: boolean;
  report_id: string | null;
}

export interface Notifications {
  pending_approvals: number;
  unread: number;
  items: NotificationItem[];
}

export const notificationsApi = {
  get: () => apiRequest<Notifications>("/api/v1/me/notifications"),
  seen: () => apiRequest<{ ok: boolean }>("/api/v1/me/notifications/seen", { method: "POST" }),
};

// ---- viewer Ask AI -------------------------------------------------------------------

export interface AskAiAnswer {
  answer: string;
  widgetsUsed: { id: string; title: string | null; type: string | null }[];
  truncated: boolean;
  suggestions: string[];
}

export const askAiApi = {
  ask: (
    reportId: string,
    body: { question: string; pageId?: string | null; filters?: unknown[]; history?: { role: "user" | "assistant"; text: string }[] },
    signal?: AbortSignal,
  ) => apiRequest<AskAiAnswer>(`/api/v1/viewer/reports/${reportId}/ai/ask`, { method: "POST", body, signal }),
};
